import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, ShieldSlash, Warning, ChartBar } from "@phosphor-icons/react";

export default function CandidatePrivacy() {
    const [consent, setConsent] = useState(null);
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [c, s] = await Promise.all([
                api.get("/me/marketplace-consent/status"),
                api.get("/me/marketplace-profile-stats"),
            ]);
            setConsent(c.data);
            setStats(s.data);
        } catch {
            setConsent({ rows: [], any_published: false });
            setStats({ total_views: 0, views_last_30d: 0, total_reveals: 0 });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const revoke = async () => {
        if (!window.confirm(
            "Revoke your consent to appear in the cross-workspace Talent Marketplace?\n\n" +
            "You'll be removed from every agency's search results. Agencies won't be able to re-publish " +
            "your profile for 30 days without new written consent from you.\n\n" +
            "Note: If you signed up on naukridaddy.com directly (organic), the platform's own listings still apply. " +
            "For a full data-erasure request, contact privacy@naukridaddy.com."
        )) return;
        setBusy(true);
        try {
            const r = await api.post("/me/marketplace-consent/revoke");
            toast.success(`Consent revoked from ${r.data.candidates_revoked} profile${r.data.candidates_revoked === 1 ? "" : "s"}.`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't revoke");
        } finally { setBusy(false); }
    };

    if (!consent || !stats) return <div className="p-10 text-[#525252]">Loading…</div>;

    const publishedRows = (consent.rows || []).filter(
        (r) => r.origin === "organic" || r.marketplace_published,
    );

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="privacy-page">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                Privacy · DPDPA · Marketplace consent
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
                Your data. Your call.
            </h1>
            <p className="text-sm text-[#525252] mb-8 max-w-2xl">
                See how often recruiters view your profile and control whether you appear in the
                cross-workspace Talent Marketplace.
            </p>

            {/* Analytics card */}
            <div data-testid="privacy-stats-card"
                 className="grid gap-4 sm:grid-cols-3 mb-8">
                <StatBox label="Recruiters viewed you" value={stats.total_views} icon={Eye} />
                <StatBox label="Last 30 days" value={stats.views_last_30d} icon={ChartBar} tone="cobalt" />
                <StatBox label="Contacts revealed" value={stats.total_reveals} icon={ShieldSlash} tone="amber" />
            </div>

            {/* Consent state */}
            <div data-testid="privacy-consent-card"
                 className={`p-6 border ${consent.any_published ? "border-emerald-300 bg-emerald-50" : "border-[#E5E5E5] bg-white"}`}>
                <h2 className="font-heading text-xl font-black text-ink mb-2">
                    {consent.any_published ? "You're visible in the Marketplace" : "You're private"}
                </h2>
                <p className="text-xs text-[#525252] max-w-lg">
                    {consent.any_published
                        ? `${publishedRows.length} profile${publishedRows.length === 1 ? "" : "s"} of yours ${publishedRows.length === 1 ? "is" : "are"} currently discoverable by recruiters across all agencies on NaukriDaddy.`
                        : "No agency has published your profile to the cross-workspace Marketplace. You appear only in the workspaces you were uploaded to."}
                </p>
                {publishedRows.length > 0 && (
                    <ul className="mt-3 text-[11px] font-mono text-[#525252] space-y-1">
                        {publishedRows.map((r) => (
                            <li key={r.id}>
                                · workspace {r.workspace_id.slice(0, 24)}… ({r.origin})
                            </li>
                        ))}
                    </ul>
                )}
                {consent.any_published && (
                    <button
                        onClick={revoke}
                        disabled={busy}
                        data-testid="privacy-revoke-btn"
                        className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-[11px] font-black uppercase tracking-wider bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                        <ShieldSlash size={13} weight="bold" />
                        {busy ? "Revoking…" : "Revoke consent · remove me"}
                    </button>
                )}
            </div>

            <div className="mt-8 p-4 border border-[#E5E5E5] bg-[#FAFAFA] flex items-start gap-3">
                <Warning size={18} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                    <p className="font-heading text-sm font-black text-ink">Full data erasure</p>
                    <p className="text-xs text-[#525252] mt-1">
                        Revoking Marketplace consent only removes you from cross-workspace visibility.
                        To permanently delete all your data across NaukriDaddy under the DPDPA, request
                        erasure at <a href="mailto:privacy@naukridaddy.com" className="text-cobalt underline">privacy@naukridaddy.com</a>.
                    </p>
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, icon: Icon, tone = "ink" }) {
    const c = tone === "cobalt" ? "text-cobalt" : tone === "amber" ? "text-amber-600" : "text-ink";
    return (
        <div className="bg-white border border-[#E5E5E5] p-5">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[#525252] mb-2">
                <Icon size={11} weight="fill" className={c} /> {label}
            </div>
            <p className={`font-heading text-4xl font-black tracking-tighter ${c}`}>{value}</p>
        </div>
    );
}
